import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, Trash2, Upload, FileUp, ShoppingCart, FileSearch, Loader2, Check, X, FileText, TrendingUp, Receipt, Edit, Eye } from "lucide-react";
import { supplierAPI, supplierOrderAPI, supplierQuoteAPI, productAPI, productFamilyAPI, productSubFamilyAPI, clientAPI, adminAPI, supplierInvoiceAPI } from "@/lib/api";
import { toast } from "sonner";
import { DateField } from "@/components/ui/date-field";

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

const ORDER_STATUSES = ["brouillon", "envoyee", "confirmee", "recue", "annulee"];
const STATUS_LABELS = { brouillon: "Brouillon", envoyee: "Envoyée", confirmee: "Confirmée", recue: "Recue", annulee: "Annulée" };
const round2 = (v) => Math.round(v * 100) / 100;

function OrdersTab({ supplierId }) {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ supplier_id: supplierId, date: new Date().toISOString().split("T")[0], expected_date: "", status: "brouillon", items: [], notes: "" });

  const load = useCallback(() => {
    supplierOrderAPI.list(supplierId).then(r => setOrders(r.data.items || r.data)).catch(() => {});
    productAPI.list({ limit: 500 }).then(r => setProducts(r.data.items || r.data)).catch(() => {});
  }, [supplierId]);
  useEffect(() => { load(); }, [load]);

  const addItem = () => setForm({ ...form, items: [...form.items, { _key: crypto.randomUUID(), product_id: "", product_name: "", quantity: 1, unit_price: 0, tva_rate: 20 }] });
  const updateItem = (idx, field, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: val };
    if (field === "product_id") {
      const p = products.find(pr => pr.id === val);
      if (p) { items[idx].product_name = p.name; items[idx].unit_price = p.unit_price; items[idx].tva_rate = p.tva_rate; }
    }
    setForm({ ...form, items });
  };
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const save = async () => {
    try {
      await supplierOrderAPI.create(form);
      toast.success("Commande créée");
      setShowDialog(false);
      setForm({ supplier_id: supplierId, date: new Date().toISOString().split("T")[0], expected_date: "", status: "brouillon", items: [], notes: "" });
      load();
    } catch { toast.error("Erreur"); }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await supplierOrderAPI.delete(id); toast.success("Supprimé"); load();
  };

  const totalHT = form.items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{orders.length} commande(s)</p>
        <Button onClick={() => setShowDialog(true)} className="bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="add-supplier-order-btn">
          <Plus size={16} className="mr-2" /> Nouvelle commande
        </Button>
      </div>
      <div className="responsive-table">
        <Table>
          <TableHeader><TableRow className="bg-slate-50"><TableHead className="uppercase text-xs text-slate-500">Numéro</TableHead><TableHead className="uppercase text-xs text-slate-500">Date</TableHead><TableHead className="uppercase text-xs text-slate-500">Statut</TableHead><TableHead className="uppercase text-xs text-slate-500 text-right">Total TTC</TableHead><TableHead className="uppercase text-xs text-slate-500 w-16">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {orders.map(o => (
              <TableRow key={o.id} className="table-row-hover">
                <TableCell className="font-mono text-sm">{o.number}</TableCell>
                <TableCell>{o.date ? new Date(o.date).toLocaleDateString("fr-FR") : "-"}</TableCell>
                <TableCell><span className={`status-badge status-${o.status}`}>{STATUS_LABELS[o.status] || o.status}</span></TableCell>
                <TableCell className="money-value">{formatEur(o.total_ttc)}</TableCell>
                <TableCell><Button variant="ghost" size="icon" onClick={() => deleteOrder(o.id)} className="text-red-500"><Trash2 size={14} /></Button></TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Aucune commande</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouvelle commande fournisseur</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Date</Label><DateField value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></div>
              <div><Label>Date prevue</Label><DateField value={form.expected_date} onChange={e => setForm({...form, expected_date: e.target.value})} /></div>
              <div><Label>Statut</Label><Select value={form.status} onValueChange={v => setForm({...form, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{ORDER_STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label>Lignes</Label><Button variant="outline" size="sm" onClick={addItem}><Plus size={14} className="mr-1" /> Ajouter</Button></div>
              {form.items.map((item, idx) => (
                <div key={item._key || idx} className="grid grid-cols-12 gap-2 items-end p-2 border rounded-md bg-slate-50 mb-2">
                  <div className="col-span-5"><Select value={item.product_id} onValueChange={v => updateItem(idx, "product_id", v)}><SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Produit..." /></SelectTrigger><SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
                  <div className="col-span-2"><Input type="number" className="h-8 text-sm" value={item.quantity} onChange={e => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)} /></div>
                  <div className="col-span-2"><Input type="number" step="0.01" className="h-8 text-sm" value={item.unit_price} onChange={e => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)} /></div>
                  <div className="col-span-2"><p className="h-8 flex items-center text-sm font-mono">{formatEur((item.quantity || 0) * (item.unit_price || 0))}</p></div>
                  <div className="col-span-1"><Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeItem(idx)}><Trash2 size={14} /></Button></div>
                </div>
              ))}
            </div>
            <div className="text-right"><span className="text-sm text-slate-500">Total HT: </span><span className="font-mono font-bold">{formatEur(totalHT)}</span></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} /></div>
            <Button onClick={save} className="w-full bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="save-supplier-order-btn">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuoteMarginEditor({ quote, supplierId, onClose, onDevisCreated }) {
  const [items, setItems] = useState([]);
  const [defaultMargin, setDefaultMargin] = useState(20);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [negativeConfirm, setNegativeConfirm] = useState(null);

  useEffect(() => {
    adminAPI.getSettings().then(r => {
      const m = parseFloat(r.data?.default_margin_percent);
      if (!isNaN(m)) setDefaultMargin(m);
    }).catch(() => {});
    clientAPI.list({ limit: 500 }).then(r => setClients(r.data.items || r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const raw = quote.extracted_data?.items || [];
    setItems(raw.map(it => {
      const purchaseHt = parseFloat(it.purchase_price_ht || it.unit_price || 0);
      const existingMargin = it.margin_percent !== undefined ? parseFloat(it.margin_percent) : defaultMargin;
      const existingSale = it.sale_price_ht !== undefined ? parseFloat(it.sale_price_ht) : round2(purchaseHt * (1 + existingMargin / 100));
      return {
        ...it,
        purchase_price_ht: round2(purchaseHt),
        quantity: parseFloat(it.quantity || 1),
        currency: it.currency || "EUR",
        validated: it.validated || false,
        margin_percent: existingMargin,
        sale_price_ht: existingSale,
        tva_rate: parseFloat(it.tva_rate || 20),
      };
    }));
  }, [quote, defaultMargin]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateMarginPercent = (idx, val) => {
    const next = [...items];
    const pct = parseFloat(val) || 0;
    next[idx] = { ...next[idx], margin_percent: pct, sale_price_ht: round2(next[idx].purchase_price_ht * (1 + pct / 100)) };
    setItems(next);
  };

  const updateSalePriceHt = (idx, val) => {
    const next = [...items];
    const sale = parseFloat(val) || 0;
    const purchase = next[idx].purchase_price_ht;
    const pct = purchase > 0 ? round2(((sale - purchase) / purchase) * 100) : 0;
    next[idx] = { ...next[idx], sale_price_ht: sale, margin_percent: pct };
    setItems(next);
  };

  const toggleValidated = (idx) => {
    const next = [...items];
    next[idx] = { ...next[idx], validated: !next[idx].validated };
    setItems(next);
  };

  const marginEur = (item) => round2((item.sale_price_ht - item.purchase_price_ht) * item.quantity);
  const totalPurchase = items.reduce((s, i) => s + i.purchase_price_ht * i.quantity, 0);
  const totalSale = items.reduce((s, i) => s + i.sale_price_ht * i.quantity, 0);
  const totalMargin = items.reduce((s, i) => s + marginEur(i), 0);
  const globalMarginPct = totalPurchase > 0 ? round2(((totalSale - totalPurchase) / totalPurchase) * 100) : 0;

  const hasNegativeMargin = items.some(i => i.margin_percent < 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await supplierQuoteAPI.updateItems(quote.id, items);
      toast.success("Marges sauvegardees");
    } catch { toast.error("Erreur sauvegarde"); }
    finally { setSaving(false); }
  };

  const handleCreateDevis = async () => {
    if (hasNegativeMargin && !negativeConfirm) {
      setNegativeConfirm(true);
      return;
    }
    if (!selectedClient) { toast.error("Veuillez selectionner un client"); return; }
    setCreating(true);
    try {
      await supplierQuoteAPI.updateItems(quote.id, items);
      const res = await supplierQuoteAPI.createDevis(quote.id, selectedClient);
      toast.success(`Devis ${res.data.number} cree`);
      setShowClientDialog(false);
      if (onDevisCreated) onDevisCreated(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur creation devis"); }
    finally { setCreating(false); setNegativeConfirm(null); }
  };

  return (
    <Card className="p-5 border-indigo-200 bg-white mb-6" data-testid="quote-margin-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="font-semibold text-slate-900">Pilotage de marge</h4>
          <p className="text-xs text-slate-500">{quote.original_filename} - {items.length} article(s)</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-margin-editor"><X size={16} /></Button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="uppercase text-[10px] text-slate-500 w-8"></TableHead>
              <TableHead className="uppercase text-[10px] text-slate-500">Produit</TableHead>
              <TableHead className="uppercase text-[10px] text-slate-500 text-right">Achat HT</TableHead>
              <TableHead className="uppercase text-[10px] text-slate-500 text-center">Qte</TableHead>
              <TableHead className="uppercase text-[10px] text-slate-500 text-center w-24">Marge %</TableHead>
              <TableHead className="uppercase text-[10px] text-slate-500 text-right w-28">Vente HT</TableHead>
              <TableHead className="uppercase text-[10px] text-slate-500 text-right">Marge (€)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item, idx) => {
              const mEur = marginEur(item);
              const isNeg = item.margin_percent < 0;
              return (
                <TableRow key={item.product_name || item.name || `margin-${idx}`} className={isNeg ? "bg-red-50" : ""}>
                  <TableCell>
                    <input type="checkbox" checked={item.validated} onChange={() => toggleValidated(idx)} className="h-3.5 w-3.5 rounded" data-testid={`margin-validate-${idx}`} />
                  </TableCell>
                  <TableCell className="text-sm font-medium max-w-[200px] truncate">{item.product_name || item.name || "-"}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatEur(item.purchase_price_ht)}</TableCell>
                  <TableCell className="text-center text-sm">{item.quantity}</TableCell>
                  <TableCell className="text-center">
                    <Input
                      type="number" step="any" className={`h-7 text-sm text-center w-20 mx-auto ${isNeg ? "border-red-400 text-red-600" : ""}`}
                      value={item.margin_percent} onChange={e => updateMarginPercent(idx, e.target.value)}
                      data-testid={`margin-pct-${idx}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number" step="0.01" className="h-7 text-sm text-right w-24 ml-auto"
                      value={item.sale_price_ht} onChange={e => updateSalePriceHt(idx, e.target.value)}
                      data-testid={`margin-sale-${idx}`}
                    />
                  </TableCell>
                  <TableCell className={`text-right font-mono text-sm font-semibold ${isNeg ? "text-red-600" : "text-emerald-600"}`}>
                    {mEur >= 0 ? "+" : ""}{formatEur(mEur)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200">
        <div className="flex gap-6 text-sm">
          <div><span className="text-slate-500">Total achat:</span> <span className="font-mono font-semibold">{formatEur(totalPurchase)}</span></div>
          <div><span className="text-slate-500">Total vente:</span> <span className="font-mono font-semibold">{formatEur(totalSale)}</span></div>
          <div><span className="text-slate-500">Marge globale:</span> <span className={`font-mono font-bold ${totalMargin >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatEur(totalMargin)} ({globalMarginPct}%)</span></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} data-testid="save-margins-btn">
            {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Check size={14} className="mr-1.5" />} Sauvegarder
          </Button>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setShowClientDialog(true)} data-testid="create-devis-from-quote-btn">
            <FileText size={14} className="mr-1.5" /> Creer devis client
          </Button>
        </div>
      </div>

      {hasNegativeMargin && (
        <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-700" data-testid="negative-margin-warning">
          Attention : une ou plusieurs lignes ont une marge negative.
        </div>
      )}

      <Dialog open={showClientDialog} onOpenChange={v => { setShowClientDialog(v); setNegativeConfirm(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Creer un devis client</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client destinataire *</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger data-testid="devis-client-select"><SelectValue placeholder="Choisir un client..." /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.company || `${c.first_name} ${c.last_name}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-slate-50 rounded-md p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Articles:</span><span>{items.length}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Total vente HT:</span><span className="font-mono font-semibold">{formatEur(totalSale)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Marge totale:</span><span className={`font-mono font-semibold ${totalMargin >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatEur(totalMargin)}</span></div>
            </div>
            {hasNegativeMargin && negativeConfirm === null && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                Ce devis contient des marges negatives. Confirmez-vous la creation ?
              </div>
            )}
            <Button
              onClick={handleCreateDevis} disabled={creating || !selectedClient}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider"
              data-testid="confirm-create-devis-btn"
            >
              {creating ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileText size={16} className="mr-2" />}
              {hasNegativeMargin && negativeConfirm === null ? "Confirmer malgre marge negative" : "Générér le devis"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PdfUploadTab({ supplierId }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [dragover, setDragover] = useState(false);
  const [families, setFamilies] = useState([]);
  const [subFamilies, setSubFamilies] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [creating, setCreating] = useState(false);
  const [activeQuote, setActiveQuote] = useState(null);
  const fileRef = useRef(null);

  const loadQuotes = useCallback(() => {
    supplierQuoteAPI.list(supplierId).then(r => setQuotes(r.data.items || r.data)).catch(() => {});
  }, [supplierId]);
  useEffect(() => {
    loadQuotes();
    productFamilyAPI.list().then(r => setFamilies(r.data)).catch(() => {});
    productSubFamilyAPI.list().then(r => setSubFamilies(r.data)).catch(() => {});
  }, [loadQuotes]);

  const handleUpload = async (file) => {
    if (!file || !file.name.endsWith(".pdf")) { toast.error("Veuillez selectionner un fichier PDF"); return; }
    setUploading(true);
    setResult(null);
    setSelectedItems({});
    try {
      const res = await supplierQuoteAPI.upload(file, supplierId);
      setResult(res.data);
      const items = res.data.extracted_data?.items || [];
      const sel = {};
      items.forEach((item, i) => {
        if (!item.already_exists) {
          sel[i] = { selected: true, family_id: "", sub_family_id: "" };
        }
      });
      setSelectedItems(sel);
      toast.success(res.data.message || "Devis analysé");
      loadQuotes();
    } catch (err) {
      toast.error("Erreur lors de l'analyse du PDF");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => { e.preventDefault(); setDragover(false); const f = e.dataTransfer.files[0]; if (f) handleUpload(f); };

  const toggleItem = (idx) => {
    setSelectedItems(prev => {
      const next = { ...prev };
      if (next[idx]) {
        next[idx] = { ...next[idx], selected: !next[idx].selected };
      } else {
        next[idx] = { selected: true, family_id: "", sub_family_id: "" };
      }
      return next;
    });
  };

  const updateItemMeta = (idx, field, val) => {
    setSelectedItems(prev => ({
      ...prev,
      [idx]: { ...prev[idx], [field]: val, ...(field === "family_id" ? { sub_family_id: "" } : {}) }
    }));
  };

  const handleCreateProducts = async () => {
    const items = result?.extracted_data?.items || [];
    const toCreate = Object.entries(selectedItems)
      .filter(([_, v]) => v.selected)
      .map(([idx, meta]) => ({
        name: items[parseInt(idx)]?.product_name || "",
        description: items[parseInt(idx)]?.description || "",
        unit_price: items[parseInt(idx)]?.unit_price || 0,
        tva_rate: items[parseInt(idx)]?.tva_rate || 20,
        family_id: meta.family_id || "",
        sub_family_id: meta.sub_family_id || "",
      }))
      .filter(p => p.name);
    if (toCreate.length === 0) { toast.error("Aucun produit selectionne"); return; }
    setCreating(true);
    try {
      const res = await productAPI.bulkCreate(toCreate);
      toast.success(res.data.message || "Produits crees");
      setResult(null);
      setSelectedItems({});
    } catch { toast.error("Erreur lors de la creation"); }
    finally { setCreating(false); }
  };

  const filteredSF = (famId) => subFamilies.filter(sf => !famId || sf.family_id === famId);

  return (
    <div>
      <div
        className={`drop-zone ${dragover ? "dragover" : ""} mb-6`}
        onDragOver={e => { e.preventDefault(); setDragover(true); }}
        onDragLeave={() => setDragover(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        data-testid="pdf-drop-zone"
      >
        <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={e => { if (e.target.files[0]) handleUpload(e.target.files[0]); }} />
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={40} className="text-indigo-600 animate-spin" />
            <p className="text-indigo-600 font-medium">Analyse du PDF en cours...</p>
            <p className="text-sm text-slate-400">L'IA extrait les donnees du devis</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <FileUp size={40} className="text-slate-400" strokeWidth={1.5} />
            <p className="text-slate-600 font-medium">Deposez un devis fournisseur PDF ici</p>
            <p className="text-sm text-slate-400">ou cliquez pour parcourir</p>
          </div>
        )}
      </div>

      {result && result.extracted_data?.items && (
        <Card className="p-4 mb-6 border-indigo-200 bg-indigo-50/50" data-testid="pdf-result">
          <h4 className="font-semibold text-indigo-900 mb-2">Résultat de l'analyse</h4>
          <p className="text-sm text-indigo-700 mb-3">{result.message}</p>
          <p className="text-xs text-slate-600 mb-3">Cochez les produits a creer et assignez les familles/sous-familles :</p>
          <div className="space-y-2 mb-4">
            {result.extracted_data.items.map((item, idx) => {
              const sel = selectedItems[idx];
              const isExisting = item.already_exists;
              return (
                <div key={item.product_name || `pdf-item-${idx}`} className={`p-3 border rounded-md ${isExisting ? 'bg-slate-50 border-slate-200' : 'bg-white border-indigo-200'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="checkbox"
                      checked={sel?.selected || false}
                      disabled={isExisting}
                      onChange={() => toggleItem(idx)}
                      className="h-4 w-4 rounded border-slate-300"
                      data-testid={`pdf-item-check-${idx}`}
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium">{item.product_name}</span>
                      {isExisting && <Badge variant="outline" className="ml-2 text-xs text-slate-500">Existe deja</Badge>}
                    </div>
                    <span className="text-sm font-mono">{formatEur(item.unit_price)} x {item.quantity}</span>
                  </div>
                  {sel?.selected && !isExisting && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-7">
                      <Select value={sel.family_id || ""} onValueChange={v => updateItemMeta(idx, "family_id", v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Famille..." /></SelectTrigger>
                        <SelectContent>{families.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={sel.sub_family_id || ""} onValueChange={v => updateItemMeta(idx, "sub_family_id", v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Sous-famille..." /></SelectTrigger>
                        <SelectContent>{filteredSF(sel.family_id).map(sf => <SelectItem key={sf.id} value={sf.id}>{sf.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <Button
            onClick={handleCreateProducts}
            disabled={creating || Object.values(selectedItems).filter(v => v.selected).length === 0}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white uppercase text-xs font-bold tracking-wider"
            data-testid="create-scanned-products-btn"
          >
            {creating ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />}
            Creer {Object.values(selectedItems).filter(v => v.selected).length} produit(s) selectionne(s)
          </Button>
        </Card>
      )}

      {activeQuote && (
        <QuoteMarginEditor
          quote={activeQuote}
          supplierId={supplierId}
          onClose={() => setActiveQuote(null)}
          onDevisCreated={() => { setActiveQuote(null); loadQuotes(); }}
        />
      )}

      <h4 className="font-semibold text-slate-900 mb-3">Devis fournisseurs scannes</h4>
      <div className="responsive-table">
        <Table>
          <TableHeader><TableRow className="bg-slate-50"><TableHead className="uppercase text-xs text-slate-500">Fichier</TableHead><TableHead className="uppercase text-xs text-slate-500">Date</TableHead><TableHead className="uppercase text-xs text-slate-500">Articles</TableHead><TableHead className="uppercase text-xs text-slate-500">Devis client</TableHead><TableHead className="uppercase text-xs text-slate-500 w-24">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {quotes.map(q => (
              <TableRow key={q.id} className="table-row-hover">
                <TableCell className="text-sm font-medium">{q.original_filename}</TableCell>
                <TableCell className="text-sm">{q.created_at ? new Date(q.created_at).toLocaleDateString("fr-FR") : "-"}</TableCell>
                <TableCell className="text-sm">{q.extracted_data?.items?.length || 0} article(s)</TableCell>
                <TableCell className="text-sm">{q.generated_devis_number ? <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300">{q.generated_devis_number}</Badge> : <span className="text-slate-400">-</span>}</TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { supplierQuoteAPI.get(q.id).then(r => setActiveQuote(r.data)).catch(() => toast.error("Erreur")); }} data-testid={`open-margin-${q.id}`}>
                    <TrendingUp size={12} className="mr-1" /> Marge
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {quotes.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Aucun devis scanne</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

const SINV_TYPE_LABELS = {
  frais_repas: "Frais de repas", deplacements: "Deplacements", loyer_charges: "Loyer & charges",
  achats_marchandises: "Achats marchandises", informatique_telecom: "Informatique & telecom",
  honoraires: "Honoraires", entretien_reparations: "Entretien & reparations",
  fournitures_bureau: "Fournitures de bureau", publicite_marketing: "Publicite & marketing",
  assurances: "Assurances", energie: "Energie", formation: "Formation",
  sous_traitance: "Sous-traitance", divers: "Divers",
};

const SINV_PAYMENT_LABELS = { cb: "CB", virement: "Virement", especes: "Espèces", cheque: "Chèque", prélèvement: "Prelev.", autre: "Autre" };

function SupplierInvoicesTab({ supplierId, supplierName }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewInvoice, setViewInvoice] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    supplierInvoiceAPI.list({ supplier_id: supplierId })
      .then(r => setInvoices(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [supplierId]);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette facture ?")) return;
    await supplierInvoiceAPI.delete(id); toast.success("Supprimee"); load();
  };

  const totalHT = invoices.reduce((s, i) => s + (i.total_ht || 0), 0);
  const totalTVA = invoices.reduce((s, i) => s + (i.tva_amount || 0), 0);
  const totalTTC = invoices.reduce((s, i) => s + (i.total_ttc || 0), 0);

  return (
    <div data-testid="supplier-invoices-tab">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">{invoices.length} facture(s)</p>
      </div>
      {loading ? (
        <div className="text-center py-8"><Loader2 size={20} className="animate-spin mx-auto text-slate-400" /></div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm">Aucune facture pour ce fournisseur</div>
      ) : (
        <>
          <div className="responsive-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="uppercase text-xs text-slate-500">Date</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500">Type</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500">Description</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500 text-right">HT</TableHead>
                  <TableHead className="uppercase text-xs text-emerald-600 text-right bg-emerald-50/50">TVA</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500 text-right">TTC</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500 text-center">Paiement</TableHead>
                  <TableHead className="uppercase text-xs text-slate-500 w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map(inv => (
                  <TableRow key={inv.id} className="table-row-hover">
                    <TableCell className="text-sm">{inv.date ? new Date(inv.date).toLocaleDateString("fr-FR") : "-"}</TableCell>
                    <TableCell className="text-sm">{SINV_TYPE_LABELS[inv.type] || inv.type}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">{inv.description || inv.invoice_number || "-"}</TableCell>
                    <TableCell className="money-value">{formatEur(inv.total_ht)}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-700 bg-emerald-50/30">{formatEur(inv.tva_amount)}</TableCell>
                    <TableCell className="money-value font-semibold">{formatEur(inv.total_ttc)}</TableCell>
                    <TableCell className="text-center text-xs text-slate-500">{SINV_PAYMENT_LABELS[inv.payment_method] || "-"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => setViewInvoice(inv)} className="text-slate-500 hover:text-indigo-600" data-testid={`view-invoice-${inv.id}`}><Eye size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(inv.id)} className="text-red-500" data-testid={`delete-invoice-${inv.id}`}><Trash2 size={14} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex gap-6 mt-3 pt-3 border-t text-sm">
            <div><span className="text-slate-500">Total HT:</span> <span className="font-mono font-semibold">{formatEur(totalHT)}</span></div>
            <div><span className="text-emerald-600">TVA:</span> <span className="font-mono font-semibold text-emerald-700">{formatEur(totalTVA)}</span></div>
            <div><span className="text-slate-500">Total TTC:</span> <span className="font-mono font-semibold">{formatEur(totalTTC)}</span></div>
          </div>
        </>
      )}

      {/* Detail invoice dialog */}
      <Dialog open={!!viewInvoice} onOpenChange={(v) => { if (!v) setViewInvoice(null); }}>
        <DialogContent className="max-w-lg" data-testid="invoice-detail-dialog" aria-describedby="invoice-detail-desc">
          {viewInvoice && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Receipt size={18} className="text-indigo-600" />
                  Detail facture
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-0.5">Fournisseur</p>
                    <p className="font-medium text-slate-900">{viewInvoice.supplier_name || supplierName}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-0.5">N° facture</p>
                    <p className="font-mono text-slate-900">{viewInvoice.invoice_number || "-"}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-0.5">Date</p>
                    <p className="text-slate-900">{viewInvoice.date ? new Date(viewInvoice.date).toLocaleDateString("fr-FR") : "-"}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-0.5">Type</p>
                    <p className="text-slate-900">{SINV_TYPE_LABELS[viewInvoice.type] || viewInvoice.type}</p>
                  </div>
                </div>
                {viewInvoice.description && (
                  <div className="text-sm">
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-0.5">Description</p>
                    <p className="text-slate-900">{viewInvoice.description}</p>
                  </div>
                )}
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Total HT</span>
                    <span className="font-mono font-semibold">{formatEur(viewInvoice.total_ht)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-600">TVA ({viewInvoice.tva_rate || 20}%)</span>
                    <span className="font-mono font-semibold text-emerald-700">{formatEur(viewInvoice.tva_amount)}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t border-slate-200">
                    <span className="font-semibold text-slate-700">Total TTC</span>
                    <span className="font-mono font-bold text-lg">{formatEur(viewInvoice.total_ttc)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-0.5">Mode de paiement</p>
                    <p className="text-slate-900">{SINV_PAYMENT_LABELS[viewInvoice.payment_method] || "-"}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-0.5">Statut</p>
                    <Badge variant={viewInvoice.is_paid ? "default" : "secondary"} className={viewInvoice.is_paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                      {viewInvoice.is_paid ? "Payée" : "Non payee"}
                    </Badge>
                  </div>
                </div>
                {viewInvoice.notes && (
                  <div className="text-sm">
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-0.5">Notes</p>
                    <p className="text-slate-700">{viewInvoice.notes}</p>
                  </div>
                )}
                {viewInvoice.items && viewInvoice.items.length > 0 && (
                  <div className="text-sm">
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-1.5">Lignes</p>
                    <div className="space-y-1">
                      {viewInvoice.items.map((item, idx) => (
                        <div key={item.description || item.product_name || `line-${idx}`} className="flex justify-between bg-white border rounded-md px-3 py-1.5 text-xs">
                          <span className="truncate flex-1">{item.description || item.product_name || `Ligne ${idx + 1}`}</span>
                          <span className="font-mono ml-3">{formatEur(item.total_ht || item.amount || 0)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}



export default function SupplierDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "orders";
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supplierAPI.get(id).then(r => { setSupplier(r.data); setLoading(false); }).catch(() => navigate("/fournisseurs"));
  }, [id, navigate]);

  if (loading || !supplier) {
    return <div className="page-container flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" /></div>;
  }

  return (
    <div className="page-container" data-testid="supplier-detail-page">
      <Button variant="ghost" onClick={() => navigate("/fournisseurs")} className="mb-4 text-slate-500" data-testid="back-to-suppliers-btn">
        <ArrowLeft size={16} className="mr-2" /> Retour aux fournisseurs
      </Button>
      <Card className="p-5 border border-slate-200 rounded-lg bg-white mb-5" data-testid="supplier-info-header">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-shrink-0">
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{supplier.company}</h2>
            {supplier.contact_name && <p className="text-sm text-slate-600">{supplier.contact_name}</p>}
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600 sm:ml-auto">
            {supplier.email && <span><span className="text-slate-400">Email:</span> {supplier.email}</span>}
            {supplier.phone && <span><span className="text-slate-400">Tel:</span> {supplier.phone}</span>}
            {(supplier.postal_code || supplier.city) && <span><span className="text-slate-400">Ville:</span> {supplier.postal_code} {supplier.city}</span>}
            {supplier.siret && <span><span className="text-slate-400">SIRET:</span> <span className="font-mono">{supplier.siret}</span></span>}
          </div>
        </div>
      </Card>
      <Card className="p-6 border border-slate-200 rounded-lg bg-white">
        <Tabs defaultValue={defaultTab}>
          <TabsList className="mb-4 flex-wrap h-auto">
            <TabsTrigger value="orders" data-testid="tab-supplier-orders"><ShoppingCart size={14} className="mr-1.5" />Commandes</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-supplier-invoices"><Receipt size={14} className="mr-1.5" />Factures</TabsTrigger>
            <TabsTrigger value="pdf" data-testid="tab-supplier-pdf"><FileSearch size={14} className="mr-1.5" />Devis PDF (IA)</TabsTrigger>
          </TabsList>
          <TabsContent value="orders"><OrdersTab supplierId={id} /></TabsContent>
          <TabsContent value="invoices"><SupplierInvoicesTab supplierId={id} supplierName={supplier.company} /></TabsContent>
          <TabsContent value="pdf"><PdfUploadTab supplierId={id} /></TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
